import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Section } from "./ui/section";
import { MoneyAmount } from "./MoneyAmount";
import { useDeleteRecurringExpense, useRecurringExpenses } from "../api/hooks";
import type { Group, RecurringExpense } from "../api/types";

interface Props {
  group: Group;
  /** Whether the caller can create/edit recurring templates (OWNER or MEMBER). */
  canManage: boolean;
}

/**
 * Section rendered on the group detail page summarising the group's
 * recurring-expense templates. Read-only for VIEWERs; gives MEMBER/OWNER
 * a New / Edit / Delete affordance for each template.
 */
export function RecurringExpensesPanel({ group, canManage }: Props): ReactNode {
  const recurringQ = useRecurringExpenses(group.id);
  const remove = useDeleteRecurringExpense(group.id);
  const items = recurringQ.data?.items ?? [];

  if (recurringQ.isLoading) {
    return (
      <Section title="Recurring expenses">
        <Skeleton className="h-24 rounded-md" />
      </Section>
    );
  }

  if (items.length === 0) {
    return (
      <Section title="Recurring expenses">
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <span className="grid size-14 place-items-center rounded-full bg-surface-variant text-outline">
            <CalendarClock className="size-6" />
          </span>
          <p className="text-body-md text-on-surface-variant">
            No recurring expenses yet.
            {canManage ? " Add one to automate rent, subscriptions, etc." : ""}
          </p>
        </div>
      </Section>
    );
  }

  return (
    <Section
      title="Recurring expenses"
      description="Templates that create a real expense on a schedule."
      contentClassName="space-y-stack-sm"
    >
      {items.map((re) => (
        <RecurringRow
          key={re.id}
          groupId={group.id}
          recurring={re}
          canManage={canManage}
          onDelete={(id) => remove.mutate(id)}
          deleting={remove.isPending}
        />
      ))}
    </Section>
  );
}

function RecurringRow({
  groupId,
  recurring,
  canManage,
  onDelete,
  deleting,
}: {
  groupId: string;
  recurring: RecurringExpense;
  canManage: boolean;
  onDelete: (id: string) => void;
  deleting: boolean;
}): ReactNode {
  const total = recurring.items.reduce((sum, it) => sum + it.cost, 0);
  const cadence = formatCadence(recurring);
  const nextRun = formatNextRun(recurring);
  const paused = recurring.status === "PAUSED";

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-outline-variant/60 p-4 transition-colors hover:border-primary/50">
      <Link
        to={
          canManage
            ? `/groups/${groupId}/recurring-expenses/${recurring.id}`
            : "#"
        }
        className={
          canManage
            ? "flex min-w-0 flex-1 items-center gap-3"
            : "pointer-events-none flex min-w-0 flex-1 items-center gap-3"
        }
      >
        <span className="grid size-10 shrink-0 place-items-center rounded-sm bg-primary-fixed text-primary">
          <CalendarClock className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-body-lg">
            <span className="truncate">{recurring.label}</span>
            {paused ? (
              <span className="shrink-0 rounded-full bg-tertiary-fixed px-2 text-label-sm text-on-tertiary-fixed">
                paused
              </span>
            ) : null}
          </p>
          <p className="text-label-md text-on-surface-variant">
            {cadence} · next: {nextRun}
          </p>
        </div>
      </Link>
      <MoneyAmount
        minor={total}
        currency={recurring.currency}
        className="shrink-0 text-headline-md"
      />
      {canManage ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Delete recurring expense ${recurring.label}`}
          disabled={deleting}
          onClick={(e) => {
            e.preventDefault();
            if (
              window.confirm(
                `Stop recurring "${recurring.label}"? Existing expenses already created stay in the group.`,
              )
            ) {
              onDelete(recurring.id);
            }
          }}
        >
          <Trash2 className="size-4" />
        </Button>
      ) : null}
    </div>
  );
}

function formatCadence(re: RecurringExpense): string {
  const unit =
    re.unit === "WEEK" ? "week" : re.unit === "MONTH" ? "month" : "year";
  const plural = re.intervalCount > 1 ? "s" : "";
  return re.intervalCount === 1
    ? `every ${unit}`
    : `every ${re.intervalCount} ${unit}${plural}`;
}

function formatNextRun(re: RecurringExpense): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(re.nextRunAt));
}
